import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Building2, Home as HomeIcon, KeyRound, UserCheck, Settings2, Eye, EyeOff, Lock } from "lucide-react";
import { toast } from "sonner";
import Lottie from "lottie-react";
import Logo from "../components/Logo";
import { useAuth } from "../context/AuthContext";
import { CITIES } from "../lib/cities";
import PasswordStrength, { scorePassword } from "../components/PasswordStrength";
import safeSecureAnim from "../assets/lottie/Safe_and_secure.json";
import userVerifiedAnim from "../assets/lottie/User_Verified.json";
import referEarnAnim from "../assets/lottie/Refer_and_Earn.json";

const ROLES = [
  { id: "broker_admin", icon: Building2, title: "Broker or Agency", sub: "I manage property deals for clients" },
  { id: "agent", icon: Building2, title: "Agent", sub: "I work independently or with a broker" },
  { id: "tenant", icon: HomeIcon, title: "Tenant", sub: "I'm looking to rent or currently renting" },
  { id: "landlord", icon: KeyRound, title: "Property Owner", sub: "I own property and rent it out" },
];

const LANDLORD_TYPES = [
  { id: "sign_agreement_only", icon: UserCheck, title: "Sign an agreement", sub: "An agent or broker has sent me an agreement to sign" },
  { id: "self_manage_properties", icon: Settings2, title: "Create my own agreements", sub: "I want to manage my own rentals and agreements" },
];

// ─── Helper: Lottie color filter ──────────────────────────────────────────────
const getLottieColorFilter = () => ({
  filter: [
    {
      id: "color-filter",
      type: "color",
      colors: {
        "#000000": "#F97316",
        "#666666": "#FFFFFF",
      },
    },
  ],
});

// ─── Helper components ───────────────────────────────────────────────────────

function StarRow() {
  return (
    <div style={{ display: "flex", gap: 2 }}>
      {[1, 2, 3, 4, 5].map((n) => (
        <svg key={n} width="12" height="12" viewBox="0 0 20 20" fill="#F97316">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
    </div>
  );
}

function Pill({ children }) {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  return (
    <div style={{
      display: "flex",
      alignItems: "flex-start",
      gap: 10,
      padding: "10px 14px",
      borderRadius: 8,
      background: "rgba(255,255,255,0.05)",
      borderLeft: "2px solid #F97316",
      fontSize: isMobile ? "0.75rem" : "0.8rem",
      color: "#D1D5DB",
      lineHeight: 1.4,
    }}>
      {children}
    </div>
  );
}

function ReviewCard({ quote, author }) {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  return (
    <div style={{
      padding: isMobile ? "12px 14px" : "14px 16px",
      borderRadius: 10,
      background: "rgba(255,255,255,0.05)",
      border: "1px solid rgba(255,255,255,0.1)",
    }}>
      <StarRow />
      <p style={{
        fontSize: isMobile ? "0.72rem" : "0.78rem",
        color: "#D1D5DB",
        lineHeight: 1.55,
        margin: "8px 0 6px",
      }}>
        "{quote}"
      </p>
      <p style={{
        fontSize: isMobile ? "0.65rem" : "0.72rem",
        color: "#6B7280",
      }}>— {author}</p>
    </div>
  );
}

const SLIDES = [
  {
    anim: safeSecureAnim,
    headline: "Not a rupee lost without proof.",
    subtext: "Landlords can only deduct what they can prove. Every claim needs photo evidence from the move-in report — the same report you both signed on day one.",
    pills: [
      "📸  Move-in photos sealed & timestamped forever",
      "✅  You approve every deduction before it moves",
      "⚖️  Disputes resolved in 10 days. Guaranteed.",
    ],
    review: {
      quote: "My landlord claimed the walls needed repainting. Orello pulled up the move-in photos. Full ₹75,000 back in 4 days.",
      author: "Sneha T., Tenant · Bandra, Mumbai",
    },
  },
  {
    anim: userVerifiedAnim,
    headline: "Your property. Documented before they move in.",
    subtext: "Room-by-room photos, timestamped and sealed the day keys are handed over. If there's damage at move-out, the evidence already exists.",
    pills: [
      "🪪  Aadhaar + PAN verified before agreement",
      "📄  Court-valid e-stamped agreement — every time",
      "📸  Property condition locked on day one — no one can edit it",
    ],
    review: {
      quote: "Tenant denied all damage at move-out. The timestamped move-in report ended the argument in one day.",
      author: "Ramesh K., Landlord · Kothrud, Pune",
    },
  },
  {
    anim: referEarnAnim,
    headline: "Every deal clean. Every client happy.",
    subtext: "Signed agreements, verified parties, documented handover — all on record. No WhatsApp arguments at 11pm. No chasing signatures.",
    pills: [
      "📋  Agreements, deposits, reports — all in one place",
      "💰  Earn up to ₹1,000 when tenant secures deposit",
      "🤝  Refer brokers — earn bonus per successful referral",
    ],
    review: {
      quote: "8 deals last month. Zero follow-up disputes. My clients refer me because the whole process just works.",
      author: "Imran K., Broker · Andheri, Mumbai",
    },
  },
];

const TRUST_MESSAGES = [
  "Your deposit is held securely by Orello. Not with the landlord. Not with us. Released only when you agree.",
  "Every tenant on Orello is Aadhaar + PAN verified before they sign. Your property, your terms — backed by a court-valid agreement.",
  "Manage your entire rental lifecycle in one place — agreements, deposits, move-in reports, move-out, settlement. Free to join.",
];

function TrustBar() {
  const [idx, setIdx] = useState(0);
  const [visible, setVisible] = useState(true);
  useEffect(() => {
    const timer = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setIdx((i) => (i + 1) % TRUST_MESSAGES.length);
        setVisible(true);
      }, 350);
    }, 4000);
    return () => clearInterval(timer);
  }, []);
  return (
    <div style={{
      marginTop: 16,
      paddingTop: 14,
      borderTop: "1px solid rgba(245,245,245,0.06)",
      display: "flex",
      alignItems: "flex-start",
      gap: 6,
      minHeight: 36,
      width: "100%",
    }}>
      <Lock size={11} style={{ color: "#6B7280", marginTop: 1, flexShrink: 0 }} />
      <p style={{
        fontSize: 11,
        color: "#9CA3AF",
        lineHeight: 1.5,
        textAlign: "left",
        transition: "opacity 0.35s ease",
        opacity: visible ? 1 : 0,
        margin: 0,
      }}>
        {TRUST_MESSAGES[idx]}
      </p>
    </div>
  );
}

function RegisterCarousel() {
  const [active, setActive] = useState(0);
  const [fading, setFading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  const goTo = useCallback((idx) => {
    if (idx === active) return;
    setFading(true);
    setTimeout(() => {
      setActive(idx);
      setFading(false);
    }, 300);
  }, [active]);

  if (isMobile) return null;

  const s = SLIDES[active];
  return (
    <div style={{
      flex: 1,
      display: "flex",
      flexDirection: "column",
      gap: 20,
      paddingLeft: 40,
      paddingRight: 20,
    }}>
      <div style={{
        flex: 1,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        opacity: fading ? 0 : 1,
        transition: "opacity 0.3s",
      }}>
        <Lottie animationData={s.anim} loop style={{ width: 220, height: 220 }} />
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        {SLIDES.map((_, i) => (
          <button key={i} onClick={() => goTo(i)} style={{
            width: 8,
            height: 8,
            borderRadius: 4,
            border: "none",
            background: i === active ? "#F97316" : "#404040",
            cursor: "pointer",
            transition: "all 0.3s",
          }} />
        ))}
      </div>
      <div style={{ opacity: fading ? 0 : 1, transition: "opacity 0.3s" }}>
        <h3 style={{ fontSize: "1.1rem", fontWeight: 600, color: "#F5F5F5", margin: "0 0 8px" }}>
          {s.headline}
        </h3>
        <p style={{ fontSize: "0.85rem", color: "#9CA3AF", margin: "0 0 12px", lineHeight: 1.5 }}>
          {s.subtext}
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 12 }}>
          {s.pills.map((p, i) => <Pill key={i}>{p}</Pill>)}
        </div>
        <ReviewCard quote={s.review.quote} author={s.review.author} />
      </div>
      <TrustBar />
    </div>
  );
}

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    password: "",
    role: params.get("role") || "tenant",
    referred_by: params.get("ref") || "",
    landlord_type: null,
    city: "",
  });
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [consent, setConsent] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleRoleSelect = (roleId) => {
    setForm({ ...form, role: roleId, landlord_type: null });
  };

  const submit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!consent) return toast.error("Please accept the Terms and Privacy Policy to continue.");
    if (form.role === "landlord" && !form.landlord_type) return toast.error("Please select how you want to use Orello.");
    if (form.password.length < 8) return toast.error("Password must be at least 8 characters.");
    const strength = scorePassword(form.password);
    if (strength.score < 3) return toast.error("Pick a stronger password (mix uppercase, numbers, symbols).");
    setBusy(true);
    const fullPhone = form.phone.startsWith("+") ? form.phone : `+91${form.phone.replace(/\D/g, "")}`;
    const self_managed = form.role === "landlord" && form.landlord_type === "self_manage_properties";
    const r = await register({ ...form, phone: fullPhone, email: form.email || undefined, self_managed });
    setBusy(false);
    if (!r.ok) {
      toast.error(r.error || "Something went wrong. Try again.");
      console.error("Register error:", r);
      return;
    }
    toast.success("Account created! Verify your phone to continue.");
    nav(`/verify-otp?phone=${encodeURIComponent(fullPhone)}`);
  };

  return (
    <div className="min-h-screen bg-[#080808] text-white flex flex-col">
      <header className="px-6 py-5 flex items-center justify-between max-w-7xl mx-auto w-full">
        <Link to="/"><Logo size="sm" forceDark /></Link>
        <div className="text-sm text-gray-400">Already have an account? <Link to="/login" className="text-[#F97316] font-medium">Sign in</Link></div>
      </header>

      <div className="flex-1 flex items-center justify-center px-4 py-8" style={{ display: "flex", alignItems: "center" }}>
        <div style={{ display: "flex", width: "100%", maxWidth: 1200, gap: 40, alignItems: "flex-start" }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="p-8 space-y-5" style={{ background: "#111111", border: "1px solid rgba(249,115,22,0.2)", borderRadius: 16, color: "#F5F5F5" }} data-testid="register-form">

            {/* Step 1 — Role selection only (for landlord) */}
            {step === 1 && (
              <>
                <div>
                  <h1 className="text-2xl font-bold font-display">Create your account</h1>
                  <p className="text-sm text-gray-400 mt-1">Join thousands of brokers, tenants, and landlords on Orello</p>
                </div>

                <form onSubmit={submit} className="space-y-5">
                  <div>
                    <label className="text-sm font-medium">Full name</label>
                    <input data-testid="reg-name" style={{ background: "#1A1A1A", color: "#F5F5F5" }} required value={form.name} onChange={set("name")} placeholder="Your name as on Aadhaar" className="mt-1 w-full bg-[#1A1A1A] text-white border border-gray-700 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#F97316] focus:border-[#F97316]" />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium">Mobile number</label>
                      <div className="mt-1 flex items-stretch border border-gray-700 rounded-lg overflow-hidden focus-within:border-[#F97316]">
                        <span className="px-3 flex items-center bg-[#1A1A1A] text-sm text-gray-400 border-r">+91</span>
                        <input data-testid="reg-phone" style={{ background: "#1A1A1A", color: "#F5F5F5" }} required value={form.phone} onChange={set("phone")} placeholder="10-digit mobile" className="flex-1 px-3 py-2.5 text-sm focus:outline-none" />
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium">Email</label>
                      <input data-testid="reg-email" style={{ background: "#1A1A1A", color: "#F5F5F5" }} type="email" required value={form.email} onChange={set("email")} placeholder="you@example.com" className="mt-1 w-full bg-[#1A1A1A] text-white border border-gray-700 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#F97316]" />
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium">Password</label>
                    <div className="mt-1 relative">
                      <input
                        data-testid="reg-password"
                        required
                        type={showPw ? "text" : "password"}
                        value={form.password}
                        onChange={set("password")}
                        placeholder="8+ characters, mix of cases, numbers, symbols"
                        autoComplete="new-password"
                        style={{ background: "#1A1A1A", color: "#F5F5F5" }}
                        className="w-full border border-gray-700 rounded-lg px-3 py-2.5 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-[#F97316]"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPw((v) => !v)}
                        style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "transparent", border: "none", color: "#9ca3af", cursor: "pointer", padding: 4 }}
                        aria-label={showPw ? "Hide password" : "Show password"}
                      >
                        {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                    <PasswordStrength password={form.password} />
                  </div>

                  <div>
                    <label className="text-sm font-medium block mb-2">I am a...</label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {ROLES.map((R) => {
                        const sel = form.role === R.id;
                        return (
                          <button type="button" key={R.id} data-testid={`role-${R.id}`}
                            onClick={() => handleRoleSelect(R.id)}
                            className={`text-left p-4 rounded-xl border-2 transition-colors ${sel ? "border-[#F97316] bg-[#1A0800]" : "border-gray-700 hover:border-gray-700"}`}>
                            <R.icon className={`h-6 w-6 ${sel ? "text-[#F97316]" : "text-gray-400"}`} />
                            <div className="mt-2 font-medium text-sm">{R.title}</div>
                            <div className="text-xs text-gray-400 mt-0.5">{R.sub}</div>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {form.role === "landlord" && (
    <div className="space-y-3">
      <label className="text-sm font-medium block">How will you use Orello?</label>
      <div className="grid grid-cols-1 gap-3">
        {LANDLORD_TYPES.map((T) => {
          const sel = form.landlord_type === T.id;
          return (
            <button type="button" key={T.id}
              onClick={() => setForm({ ...form, landlord_type: T.id })}
              className={`text-left p-4 rounded-xl border-2 transition-colors ${sel ? "border-[#F97316] bg-[#1A0800]" : "border-gray-700 hover:border-gray-700"}`}>
              <T.icon className={`h-6 w-6 ${sel ? "text-[#F97316]" : "text-gray-400"}`} />
              <div className="mt-2 font-medium text-sm">{T.title}</div>
              <div className="text-xs text-gray-400 mt-0.5">{T.sub}</div>
            </button>
          );
        })}
      </div>
    </div>
  )}

  {(form.role === "agent" || form.role === "broker_admin") && (
    <div>
      <label className="text-sm font-medium block mb-1">Your city</label>
      <select
        data-testid="reg-city"
        value={form.city}
        onChange={set("city")}
        style={{ background: "#1A1A1A", color: "#F5F5F5" }}
        className="w-full border border-gray-700 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#F97316] focus:border-[#F97316]"
      >
        <option value="">— Select your city —</option>
        {CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
      </select>
      <p className="text-xs text-gray-500 mt-1">Used to rank you on the local leaderboard. You can change this anytime in Settings.</p>
    </div>
  )}

  {(form.role !== "landlord" || form.landlord_type) && (
    <>
      <div className="flex items-start gap-3">
                        <input type="checkbox" id="consent" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-gray-700 text-[#F97316] cursor-pointer" />
                        <label htmlFor="consent" className="text-[12px] text-gray-400 leading-relaxed cursor-pointer">
                          I have read and agree to the{" "}
                          <a href="/terms" target="_blank" className="text-[#F97316] underline">Terms & Conditions</a>
                          {" "}and{" "}
                          <a href="/privacy" target="_blank" className="text-[#F97316] underline">Privacy Policy</a>
                          {" "}including data processing under India's DPDP Act 2023.
                        </label>
                      </div>
                      <button data-testid="reg-submit" disabled={busy} className="w-full h-12 bg-[#F97316] hover:bg-[#EA580C] btn-press text-white rounded-lg font-medium disabled:opacity-50">
                        {busy ? "Creating…" : "Create Account"}
                      </button>
                    </>
                  )}
                </form>
              </>
            )}

            {/* Step 2 — Landlord type selection */}
            {step === 2 && (
              <div className="space-y-5">
                <div>
                  <button onClick={() => setStep(1)} className="text-sm text-gray-400 hover:text-gray-300 mb-3">← Back</button>
                  <h1 className="text-2xl font-bold font-display">How will you use Orello?</h1>
                  <p className="text-sm text-gray-400 mt-1">This helps us set up the right experience for you.</p>
                </div>

                <div className="grid grid-cols-1 gap-3">
                  {LANDLORD_TYPES.map((T) => {
                    const sel = form.landlord_type === T.id;
                    return (
                      <button type="button" key={T.id}
                        onClick={() => setForm({ ...form, landlord_type: T.id })}
                        className={`text-left p-5 rounded-xl border-2 transition-colors ${sel ? "border-[#F97316] bg-[#1A0800]" : "border-gray-700 hover:border-gray-700"}`}>
                        <T.icon className={`h-6 w-6 ${sel ? "text-[#F97316]" : "text-gray-400"}`} />
                        <div className="mt-2 font-medium">{T.title}</div>
                        <div className="text-xs text-gray-400 mt-0.5">{T.sub}</div>
                      </button>
                    );
                  })}
                </div>

                {form.landlord_type && (
                  <>
                    <div className="flex items-start gap-3">
                      <input type="checkbox" id="consent2" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-gray-700 text-[#F97316] cursor-pointer" />
                      <label htmlFor="consent2" className="text-[12px] text-gray-400 leading-relaxed cursor-pointer">
                        I have read and agree to the{" "}
                        <a href="/terms" target="_blank" className="text-[#F97316] underline">Terms & Conditions</a>
                        {" "}and{" "}
                        <a href="/privacy" target="_blank" className="text-[#F97316] underline">Privacy Policy</a>
                        {" "}including data processing under India's DPDP Act 2023.
                      </label>
                    </div>
                    <button onClick={(e) => submit(e)} disabled={busy} className="w-full h-12 bg-[#F97316] hover:bg-[#EA580C] btn-press text-white rounded-lg font-medium disabled:opacity-50">
                      {busy ? "Creating…" : "Create Account"}
                    </button>
                  </>
                )}
              </div>
            )}

            </div>
          </div>
          <RegisterCarousel />
        </div>
      </div>
    </div>
  );
}